import { useCurrentPageAccess } from "@/app/router";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { useDialog } from "@/shared/hooks/useDialog";
import { toast } from "@/shared/hooks/useToast";
import { RotateCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FacebookResourceTargetDateDialog } from "../components/FacebookResourceTargetDateDialog";
import { useFacebookResourceTargetDateColumns } from "../hooks/useFacebookResourceTargetDateColumns";
import type { FacebookResourceTargetDateRow } from "../types/preferences_types";
import type { SortingState } from "@tanstack/react-table";
import {
  useFacebookResourceTargetDatesQuery,
  useUpdateDefaultBackwardDaysMutation,
  useUpsertTargetDateMutation,
  useDeleteTargetDatesMutation,
} from "../api/facebookResourceTargetDates";

function isValidYmd(s: string) {
  // 간단 검증 (YYYY-MM-DD)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime());
}

export function PreferencesFacebookResourceDatePage() {
  // React Query 훅들
  const { data, isLoading, refetch } = useFacebookResourceTargetDatesQuery();
  const updateDefaultMutation = useUpdateDefaultBackwardDaysMutation();
  const upsertMutation = useUpsertTargetDateMutation();
  const deleteMutation = useDeleteTargetDatesMutation();

  const columns = useFacebookResourceTargetDateColumns();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const [selectedRows, setSelectedRows] = useState<FacebookResourceTargetDateRow[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  const addDialog = useDialog<void>();
  const editDialog = useDialog<FacebookResourceTargetDateRow>();
  const deleteDialog = useDialog<void>();

  const defaultBackwardDays = data?.defaultBackwardDays ?? 7;
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const [tempDefaultDays, setTempDefaultDays] = useState(String(defaultBackwardDays));

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  // API 데이터가 로드되면 tempDefaultDays 업데이트
  useEffect(() => {
    if (data) {
      setTempDefaultDays(String(data.defaultBackwardDays));
    }
  }, [data]);

  // 정렬된 데이터
  const sortedData = useMemo(() => {
    if (sorting.length === 0) return rows;

    const sorted = [...rows];
    const sort = sorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // 숫자 타입인 경우 숫자로 비교
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sort.desc ? bValue - aValue : aValue - bValue;
      }

      // 객체 타입인 경우 JSON 문자열로 비교
      if (typeof aValue === "object" || typeof bValue === "object") {
        const aStr = JSON.stringify(aValue);
        const bStr = JSON.stringify(bValue);
        if (aStr < bStr) return sort.desc ? 1 : -1;
        if (aStr > bStr) return sort.desc ? -1 : 1;
        return 0;
      }

      // 문자열 타입인 경우 문자열로 비교
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return sort.desc ? 1 : -1;
      if (aStr > bStr) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [rows, sorting]);

  // 페이지네이션 핸들러
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // 페이지 크기 변경 시 첫 페이지로 이동
  };

  // 현재 페이지에 표시할 데이터 계산
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleUpdateDefault = async () => {
    const value = Number(tempDefaultDays);
    if (!Number.isInteger(value) || value < 1) {
      toast({ title: "1 이상의 정수를 입력해주세요.", variant: "destructive" });
      return;
    }
    await updateDefaultMutation.mutateAsync(value);
    // 데이터 리페치
    await refetch();
    toast({ title: "저장 완료", description: "기본 과거 데이터 조회일 Window가 수정되었습니다." });
  };

  const handleUpsert = async (adAccountId: string, targetDate: string, mode: "create" | "edit") => {
    setErrorMsg("");

    if (!isValidYmd(targetDate)) {
      setErrorMsg(
        "Target date의 형식이 올바르지 않습니다. YYYY-MM-DD의 형식으로 다시 입력해 주세요."
      );
      return { ok: false as const };
    }

    if (mode === "create") {
      const exists = rows.some((r) => r.adAccountId === adAccountId);
      if (exists) {
        setErrorMsg("동일한 ad_account_id가 이미 존재합니다.");
        return { ok: false as const };
      }
    }

    await upsertMutation.mutateAsync({ adAccountId, targetDate });
    return { ok: true as const };
  };

  const handleDelete = async (adAccountIds: string[]) => {
    await deleteMutation.mutateAsync(adAccountIds);
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="페이스북 리소스 과거 데이터 비교일자 설정"
        description="페이스북 리소스 수집 시 과거 데이터와의 비교일자를 관리합니다. 특정 일자에 수집된 리소스들의 id를 비교하여 이미 수집된 리소스를 수집하지 않게 할 수 있습니다."
      />

      {/* Default window */}
      <Card className="w-[360px] p-4 space-y-3">
        <div className="text-base font-semibold">기본 과거 리소스 데이터 조회일 Window</div>

        <div className="flex items-center gap-2">
          <Input
            value={tempDefaultDays}
            onChange={(e) => setTempDefaultDays(e.target.value)}
            maxLength={4}
            disabled={isLoading || updateDefaultMutation.isPending}
          />
          <span className="text-sm text-muted-foreground">일</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleUpdateDefault}
            disabled={isLoading || updateDefaultMutation.isPending || !canWriteCurrentPage}
          >
            수정
          </Button>
          <Button
            variant="outline"
            onClick={() => setTempDefaultDays(String(defaultBackwardDays))}
            disabled={isLoading || updateDefaultMutation.isPending}
          >
            취소
          </Button>
        </div>
      </Card>

      {/* target_date list */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">로딩 중...</p>
          </div>
        ) : (
          <DataTable
            key={rows.length}
            columns={columns}
            data={paginatedData}
            totalCount={sortedData.length}
            page={page}
            pageSize={pageSize}
            sorting={sorting}
            onSortingChange={setSorting}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableRowSelection
            rowSelectionTrigger="checkbox"
            getRowId={(row) => row.adAccountId}
            onRowSelectionChange={setSelectedRows}
            onRowClick={(row) => editDialog.open(row)}
            toolbar={
              <DataTableToolbar
                filters={
                  <div className="text-base font-semibold">
                    광고주별 과거 리소스 데이터 조회 target_date 지정
                  </div>
                }
                actions={
                  <>
                    <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
                      <RotateCw className="mr-2 h-4 w-4" />
                      새로고침
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setErrorMsg("");
                        addDialog.open();
                      }}
                      disabled={isLoading || !canWriteCurrentPage}
                    >
                      추가
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={selectedRows.length === 0 || isLoading || !canWriteCurrentPage}
                      onClick={() => deleteDialog.open()}
                    >
                      삭제
                    </Button>
                  </>
                }
              />
            }
          />
        )}
      </div>

      <FacebookResourceTargetDateDialog
        open={addDialog.isOpen}
        onOpenChange={(open) => !open && addDialog.close()}
        mode="create"
        errorMsg={errorMsg}
        onClearError={() => setErrorMsg("")}
        onSubmit={async ({ adAccountId, targetDate }) => {
          const res = await handleUpsert(adAccountId, targetDate, "create");
          if (res.ok) {
            addDialog.close();
            // 다이얼로그가 닫힌 후 데이터 리페치
            await refetch();
            toast({ title: "저장 완료", description: "target_date가 저장되었습니다." });
          }
        }}
      />

      <FacebookResourceTargetDateDialog
        open={editDialog.isOpen}
        onOpenChange={(open) => !open && editDialog.close()}
        mode="edit"
        initial={editDialog.data}
        errorMsg={errorMsg}
        onClearError={() => setErrorMsg("")}
        onSubmit={async ({ adAccountId, targetDate }) => {
          const res = await handleUpsert(adAccountId, targetDate, "edit");
          if (res.ok) {
            editDialog.close();
            // 다이얼로그가 닫힌 후 데이터 리페치
            await refetch();
            toast({ title: "저장 완료", description: "target_date가 저장되었습니다." });
          }
        }}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="삭제"
        description={`선택한 ${selectedRows.length}개의 ad_account_id target_date를 삭제하시겠습니까?`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={async () => {
          await handleDelete(selectedRows.map((r) => r.adAccountId));
          setSelectedRows([]);
          deleteDialog.close();
          // 다이얼로그가 닫힌 후 데이터 리페치
          await refetch();
          toast({ title: "삭제 완료", description: "target_date가 삭제되었습니다." });
        }}
      />
    </div>
  );
}
